import type { FlightOccurrence, FlightPlan, PlayerRotation, ScheduleSource } from "../types";


export type ScheduleLegSelector = Pick<PlayerRotation["legs"][number], "occurrenceId" | "scheduledFlightId">;

export type ResolvedScheduleSelector = {
  selectorId: string;
  scheduledFlightId?: string;
  flightPlan?: FlightPlan;
  firstOccurrence?: FlightOccurrence;
};

export function resolveScheduleSelector(id: string, schedule: ScheduleSource): ResolvedScheduleSelector {
  const occurrence = schedule.occurrences.find((item) => item.id === id);
  const plan = flightPlanForSelector(id, occurrence, schedule);
  if (!plan) {
    return { selectorId: id };
  }
  const scheduledFlightId = plan.id;
  const firstOccurrence =
    occurrence ??
    schedule.occurrences
      .filter((item) => occurrenceMatchesRotationLeg(item, { scheduledFlightId }, schedule))
      .sort((a, b) => a.day - b.day || a.departureTimeLocal.localeCompare(b.departureTimeLocal))[0];
  return { selectorId: scheduledFlightId, scheduledFlightId, flightPlan: plan, firstOccurrence };
}

function flightPlanForSelector(id: string, occurrence: FlightOccurrence | undefined, schedule: ScheduleSource): FlightPlan | undefined {
  if (occurrence) {
    return schedule.flightPlans.find((item) => item.id === occurrence.flightPlanId);
  }
  return schedule.flightPlans.find((item) => item.id === id);
}

export function rotationLegSelectorId(leg: ScheduleLegSelector): string | undefined {
  return leg.scheduledFlightId ?? leg.occurrenceId;
}

export function occurrenceSelectorId(occurrenceId: string, schedule: ScheduleSource): string {
  const occurrence = schedule.occurrences.find((item) => item.id === occurrenceId);
  return occurrence?.flightPlanId ?? occurrenceId;
}

export function rotationSelectorsOverlap(left: string, right: string): boolean {
  return left === right;
}

export function resolveOccurrenceForLeg(
  leg: (PlayerRotation["legs"][number] & Partial<ScheduleLegSelector>) | undefined,
  schedule: ScheduleSource,
  cycleStartDay?: number
): FlightOccurrence | undefined {
  if (!leg) return undefined;
  const targetDay = rotationTargetDay(cycleStartDay, leg.departureDayOffset);
  return schedule.occurrences
    .filter((occurrence) => occurrenceMatchesRotationLeg(occurrence, leg, schedule))
    .filter((occurrence) => targetDay === undefined || occurrence.day === targetDay)
    .sort((a, b) => a.day - b.day || a.departureTimeLocal.localeCompare(b.departureTimeLocal))[0];
}

function rotationTargetDay(cycleStartDay: number | undefined, departureDayOffset: number): number | undefined {
  if (cycleStartDay === undefined) {
    return undefined;
  }
  return cycleStartDay + departureDayOffset;
}

export function resolveOccurrenceForLegOnDay(
  leg: PlayerRotation["legs"][number] | undefined,
  schedule: ScheduleSource,
  day: number
): FlightOccurrence | undefined {
  if (!leg) return undefined;
  return schedule.occurrences
    .filter((occurrence) => occurrence.day === day && occurrenceMatchesRotationLeg(occurrence, leg, schedule))
    .sort((a, b) => a.departureTimeLocal.localeCompare(b.departureTimeLocal))[0];
}

export function resolveFlightPlanForLeg(leg: ScheduleLegSelector | undefined, schedule: ScheduleSource): FlightPlan | undefined {
  if (!leg) return undefined;
  return schedule.flightPlans.find((plan) => plan.id === leg.scheduledFlightId);
}

export function occurrenceMatchesRotationLeg(occurrence: FlightOccurrence, leg: ScheduleLegSelector, _schedule: ScheduleSource): boolean {
  if (leg.occurrenceId && occurrence.id === leg.occurrenceId) return true;
  return Boolean(leg.scheduledFlightId && occurrence.flightPlanId === leg.scheduledFlightId);
}
